import { useUpdateMeetingStatus } from '@actions';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import Select from '@components/V4/Select/Select';
import { useAuthState } from '@contexts/AuthContext';
import { MEETING_STATUSES, MEETING_STATUSES_OPTIONS, SESSION_STATUSES } from '@shared/constants';
import { MeetingStatus, UserRole } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import MeetingDetail from './MeetingDetail';

interface MeetingStatusEditorProps {
  meetingId: string;
  status: MeetingStatus;
}

const MeetingStatusEditor = (props: MeetingStatusEditorProps) => {
  const { meetingId, status } = props;

  const { roles } = useAuthState();
  const isSuperUser = roles?.includes(UserRole.SuperUser);

  const toast = useToast();
  const query = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<MeetingStatus>(status);
  const [paidSessionWarning, setPaidSessionWarning] = useState<string | undefined>();

  const { mutate: updateStatus, isLoading } = useUpdateMeetingStatus(meetingId, {
    onSuccess: (data) => {
      query.invalidateQueries('/enrollments/:id');

      toast.success(
        data.sessionStatusChanged
          ? `Meeting set to ${MEETING_STATUSES[data.status]}. Session moved to ${
              SESSION_STATUSES[data.sessionStatus]
            }.`
          : `Meeting set to ${MEETING_STATUSES[data.status]}. Session status left unchanged.`
      );

      setPaidSessionWarning(undefined);
      setIsEditing(false);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message;

      if (error?.response?.status === 409) {
        setPaidSessionWarning(message);
        return;
      }

      toast.error(message ?? 'The meeting status could not be updated.');
    },
  });

  if (!isSuperUser) {
    return <MeetingDetail name="Meeting Status:" value={MEETING_STATUSES[status]} />;
  }

  if (!isEditing) {
    return (
      <div className="flex gap-2 items-center">
        <MeetingDetail name="Meeting Status:" value={MEETING_STATUSES[status]} />
        <Button
          variant="secondary"
          size="normal"
          className="h-5 min-h-5 text-xs"
          buttonText="Change"
          onClick={() => {
            setSelectedStatus(status);
            setIsEditing(true);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-2 rounded-lg bg-surfaceHover">
      <Select
        label="Meeting Status"
        value={selectedStatus}
        options={MEETING_STATUSES_OPTIONS}
        onSelect={(value) => setSelectedStatus(value as MeetingStatus)}
      />

      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          className="h-7"
          buttonText="Cancel"
          onClick={() => setIsEditing(false)}
        />
        <Button
          variant="primary"
          className="h-7"
          isLoading={isLoading}
          disabled={selectedStatus === status}
          buttonText="Save"
          onClick={() => updateStatus({ status: selectedStatus })}
        />
      </div>

      {!!paidSessionWarning && (
        <Modal isOpen size="small" onClose={() => setPaidSessionWarning(undefined)}>
          <Modal.Header title="This session is already paid" />
          <Modal.Body className="flex flex-col gap-4 text-xs text-white">
            <p>{paidSessionWarning}</p>
            <p>
              The meeting status will be corrected, but the session stays <b>Paid</b> so the report
              it was paid in keeps matching.
            </p>
          </Modal.Body>
          <Modal.Footer className="flex justify-end gap-4">
            <Button
              variant="secondary"
              className="h-7"
              buttonText="Cancel"
              onClick={() => setPaidSessionWarning(undefined)}
            />
            <Button
              variant="primary"
              className="h-7"
              isLoading={isLoading}
              buttonText="Change meeting only"
              onClick={() => updateStatus({ status: selectedStatus, force: true })}
            />
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default MeetingStatusEditor;
